import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { X, Heart } from "lucide-react";

const SEEN_KEY = "gh_newsletter_seen";
const DONE_KEY = "gh_newsletter_done";
const SNOOZE_DAYS = 7;

export const NewsletterPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const dismissedRef = useRef(false);

  useEffect(() => {
    if (localStorage.getItem(DONE_KEY)) return;
    const seenAt = Number(localStorage.getItem(SEEN_KEY) || 0);
    if (seenAt && Date.now() - seenAt < SNOOZE_DAYS * 86400000) return;

    const show = () => {
      if (dismissedRef.current || localStorage.getItem(DONE_KEY)) return;
      setOpen(true);
    };
    const t = setTimeout(show, 14000);
    const onExit = (e) => { if (e.clientY <= 0) show(); };
    document.addEventListener("mouseleave", onExit);
    return () => { clearTimeout(t); document.removeEventListener("mouseleave", onExit); };
  }, []);

  useEffect(() => { if (open) localStorage.setItem(SEEN_KEY, String(Date.now())); }, [open]);

  const close = () => {
    dismissedRef.current = true;
    localStorage.setItem(SEEN_KEY, String(Date.now()));
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email, origin: window.location.origin });
      localStorage.setItem(DONE_KEY, "1");
      localStorage.setItem("gh_email", email.toLowerCase());
      dismissedRef.current = true;
      toast.success("Welcome to the family — check your inbox 💛");
      setOpen(false);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#3A3632]/60 backdrop-blur-sm" data-testid="newsletter-popup" onClick={close}>
      <div className="relative bg-[#FAF7F2] rounded-2xl max-w-md w-full overflow-hidden border border-[#D9CBA0] shadow-2xl gh-fade-up" onClick={(e) => e.stopPropagation()}>
        <button onClick={close} data-testid="newsletter-close" className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-[#E5DFD5] transition-colors z-10"><X className="h-5 w-5 text-[#8A8175]" /></button>
        <div className="bg-[#8E9A86]/12 py-7 text-center border-b border-[#D9CBA0]/40">
          <div className="h-12 w-12 gh-octagon bg-[#8E9A86]/20 flex items-center justify-center mx-auto"><Heart className="h-6 w-6 text-[#8E9A86]" /></div>
        </div>
        <div className="p-8 text-center">
          <span className="gh-overline">Join Hope &amp; Favor</span>
          <h3 className="font-heading text-3xl text-[#3A3632] mt-2 leading-snug">Comfort &amp; hope, straight to your inbox.</h3>
          <p className="text-sm text-[#6B655F] mt-3 leading-relaxed">Gentle encouragement, gift-giving ideas, and first word on new Care Boxes &amp; the Hope Cards series.</p>
          <form onSubmit={submit} className="mt-5">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="newsletter-email"
              placeholder="you@example.com" className="w-full border border-[#D1CCC5] rounded-lg px-4 py-3 bg-white outline-none focus:border-[#8E9A86] focus:ring-2 focus:ring-[#8E9A86]/30 text-center" />
            <button type="submit" disabled={busy} data-testid="newsletter-submit" className="w-full mt-3 bg-[#8E9A86] text-white rounded-full px-6 py-3 font-medium hover:bg-[#7A8772] transition-colors disabled:opacity-60">
              {busy ? "Joining…" : "Send me a little hope 💛"}
            </button>
          </form>
          <button onClick={close} data-testid="newsletter-dismiss" className="text-xs text-[#8A8175] mt-4 hover:text-[#3A3632]">No thanks, maybe later</button>
        </div>
      </div>
    </div>
  );
};
